import { computed, inject } from '@angular/core';
import { updateState, withDevtools, withFeatureFactory } from '@angular-architects/ngrx-toolkit';
import { tapResponse } from '@ngrx/operators';
import { signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { setPageNumber, setPageSize, withPagination, withPaginationMethods } from '@shared/state/pagination.feature';
import { setError, setFulfilled, setPending, withRequestStatus } from '@shared/state/request-status.feature';
import { pipe, switchMap, tap } from 'rxjs';

import { OrganizationAdmin } from '../model/organization-admin.model';
import { FilterOrganizationRequest } from '../model/organization-filter-request.model';
import { UserService } from '../services/user.service';

export const OrganizationStore = signalStore(
  withDevtools('admin-organizations'),
  withState({
    data: null as OrganizationAdmin[] | null,
  }),
  withRequestStatus(),
  withFeatureFactory((store) => withPagination<OrganizationAdmin>(store.data)),
  withComputed(({ data }) => ({
    organizations: computed(() => data()),
  })),
  withMethods((store, userService = inject(UserService)) => ({
    load: rxMethod<FilterOrganizationRequest>(
      pipe(
        tap(({ pageNumber, pageSize, enableGlobalLoader }) => {
          if (!enableGlobalLoader) {
            updateState(store, '[OrganizationStore] Set pending', setPending());
          }
          updateState(store, '[OrganizationStore] Set page number', setPageNumber(pageNumber));
          updateState(store, '[OrganizationStore] Set page size', setPageSize(pageSize));
        }),
        switchMap(({ ordering, sortDirection, enableGlobalLoader }) =>
          userService
            .getOrganizations({
              ordering,
              sortDirection,
              pageSize: store.pageSize(),
              pageNumber: store.pageNumber(),
              enableGlobalLoader,
            })
            .pipe(
              tapResponse({
                next: (data) => {
                  updateState(store, '[OrganizationStore] Set fulfilled', setFulfilled());
                  updateState(store, '[OrganizationStore] Set data', { data });
                },
                error: () => {
                  updateState(store, '[OrganizationStore] Set error', setError());
                  updateState(store, '[OrganizationStore] Set data', { data: null });
                },
              }),
            ),
        ),
      ),
    ),
  })),
  withFeatureFactory((store) => withPaginationMethods(store)),
);
